import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Card, Col, Row, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useGetContractDetailsQuery } from "../../../app/features/apiSlice";
import Loader from "../../../components/Loader";
import Error from "../../../components/Error";
import { setPageTitle } from "../../../app/features/DataSlice";
import CurrencyValue from "../../../components/CurrencyValue";
// import UpdateInitialIngot from "../../../components/UpdateInitialIngot";
import ContractDetailsProfile from "../../../components/HeliorPlan/ContractDetailsProfile";
import moment from "moment";

const ProfileContractDetail = () => {
  const pageTitle = "Plan";
  const { t } = useTranslation();
  const { contractCode } = useParams();
  const dispatch = useDispatch();
  const sessionId = useRef(Date.now()).current;

  const { data, isLoading, isError, error, isSuccess } =
    useGetContractDetailsQuery({ sessionId, contractCode });

  const contractCurrency = data?.data?.currency;
  const contractRate = data?.data?.fx_rate;

  useEffect(() => {
    dispatch(setPageTitle(pageTitle));
  });

  return (
    <div className="my-3">
      {isLoading && <Loader />}
      {isError && <Error error={error} />}
      {isSuccess && (
        <Card className="mb-2" bg="dark1 text-light">
          <Card.Body>
            <Row className="contract-row">
              {/* ================= DETAILS ================= */}
              <Col xs="12" lg="9" className="contract-details">
                <Row>

                  {/* Column 1 */}
                  <Col xs="12" md="4">
                    <ul className="list-unstyled m-0">

                      <li className="my-2 d-flex justify-content-between align-items-start">
                        <b className="w-50 text-break lh-sm">{t("date")}:</b>
                        <span className="w-50 text-end text-break">
                          {data?.data?.contract_date
                            ? moment(data?.data?.contract_date).format("DD-MM-YYYY")
                            : ""}
                        </span>
                      </li>

                      <li className="my-2 d-flex justify-content-between align-items-start">
                        <b className="w-50 text-break lh-sm">{t("periodicity")}:</b>
                        <span className="w-50 text-end text-break">
                          {t(data?.data?.periodicity_of_amount)}
                        </span>
                      </li>

                      <li className="my-2 d-flex justify-content-between align-items-start">
                        <b className="w-50 text-break lh-sm">{t("paymentAmount")}:</b>
                        <span className="w-50 text-end text-break">
                          <CurrencyValue
                            currency={contractCurrency}
                            rate={contractRate}
                            amount={data?.data?.payment_amount}
                          />
                        </span>
                      </li>

                      <li className="my-2 d-flex justify-content-between align-items-start">
                        <b className="w-50 text-break lh-sm">{t("initialPayment")}:</b>
                        <span className="w-50 text-end text-break">
                          {data?.data?.number_of_initial_payments}
                        </span>
                      </li>

                    </ul>
                  </Col>

                  {/* Column 2 */}
                  <Col xs="12" md="4">
                    <ul className="list-unstyled m-0">

                      <li className="my-2 d-flex justify-content-between align-items-start">
                        <b className="w-50 text-break lh-sm">{t("initialIngot")}:</b>
                        <span className="w-50 text-end text-break">
                          {data?.data?.initial_ingot_gr} gr
                        </span>
                      </li>

                      <li className="my-2 d-flex justify-content-between align-items-start">
                        <b className="w-50 text-break lh-sm">{t("recurringPayments")}:</b>
                        <span className="w-50 text-end text-break">
                          {data?.data?.number_of_recurring_payments}
                        </span>
                      </li>

                      <li className="my-2 d-flex justify-content-between align-items-start">
                        <b className="w-50 text-break lh-sm">{t("recurringIngot")}:</b>
                        <span className="w-50 text-end text-break">
                          {data?.data?.ingot_gr} gr
                        </span>
                      </li>

                      <li className="my-2 d-flex justify-content-between align-items-start">
                        <b className="w-50 text-break lh-sm">{t("totalPayment2")}:</b>
                        <span className="w-50 text-end text-break">
                          <CurrencyValue
                            currency={contractCurrency}
                            rate={contractRate}
                            amount={data?.data?.recurring_amount_paid}
                          />
                        </span>
                      </li>

                    </ul>
                  </Col>

                  {/* Column 3 */}
                  <Col xs="12" md="4">
                    <ul className="list-unstyled m-0">

                      <li className="my-2 d-flex justify-content-between align-items-start">
                        <b className="w-50 text-break lh-sm">{t("quotaDoc")}:</b>
                        <span className="w-50 text-end text-break">
                          <CurrencyValue
                            currency={contractCurrency}
                            rate={contractRate}
                            amount={data?.data?.doc_share}
                          />
                        </span>
                      </li>

                      <li className="my-2 d-flex justify-content-between align-items-start">
                        <b className="w-50 text-break lh-sm">{t("recurringAmountPaid")}:</b>
                        <span className="w-50 text-end text-break">
                          <CurrencyValue
                            currency={contractCurrency}
                            rate={contractRate}
                            amount={data?.data?.total_payment}
                          />
                        </span>
                      </li>

                      {data?.data?.disc_code && (
                        <li className="my-2 d-flex justify-content-between align-items-start text-gold">
                          <b className="w-50 text-break lh-sm">{t("couponCode")}:</b>
                          <span className="w-50 text-end text-break">
                            {data?.data?.disc_code}
                          </span>
                        </li>
                      )}

                    </ul>
                  </Col>

                </Row>
              </Col>

              {/* ================= CONTRACT INFO ================= */}
              <Col xs="12" lg="3" className="contract-info">
                <div className="h-100 d-flex justify-content-start justify-content-lg-end align-items-center">
                  <i className="fa fa-cart-shopping bg-gold text-light p-3 rounded-circle font-40" />

                  <div className="m-3">
                    <h6 className="mb-0">{contractCode}</h6>

                    {data?.data?.docSplitRequest === "Pending" ? (
                      <p className="text-light mb-0 lh-sm">
                        {t("docSplitRequest")}: {data?.data?.docSplitRequest}
                      </p>
                    ) : (
                      <Badge
                        bg={data?.data?.status === "Active" ? "success" : "danger"}
                      >
                        {data?.data?.status === "Active"
                          ? t("active")
                          : t("inactive")}
                      </Badge>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <ContractDetailsProfile
        payments={data?.data?.payment}
        contractCode={contractCode}
        currency={contractCurrency}
        rate={contractRate}
      />
    </div>
  );
};

export default ProfileContractDetail;
